// src/pages/client/ClientDashboard.jsx
// Client Admin Dashboard - works with both URL params and subdomain routing
//
// Access patterns:
// - invenire.app/client/supabase → tenantKey from URL param
// - supabase.invenire.app/admin → tenantKey from subdomain (prop)

import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { trackPageView } from "../../utils/analytics";
import PlanUsage from "../../components/PlanUsage";

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "https://api.invenire.app";

// Card accent colors
const COLORS = {
  green: "#3ECF8E",
  blue: "#3b82f6",
  purple: "#a855f7",
  red: "#f87171",      // Soft red for Configuration
  orange: "#f59e0b"
};

export default function ClientDashboard({ tenantKeyFromSubdomain }) {
  // Get tenantKey from URL params OR from subdomain prop
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determine if we're on subdomain (affects link paths)
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  
  // Extract branding for dynamic styles (memoized to avoid dependency issues)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || COLORS.green;
  const companyName = branding.companyName || tenant?.name || tenantKey;
  
  // Get backgroundColor from branding (matches ClientConfigPage approach)
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark for text color
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
    return false;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding (matching ClientConfigPage exactly)
  const dynamicStyles = useMemo(() => {
    const textColor = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const buttonBg = branding.buttonPrimaryBg || primaryColor;
    const buttonText = branding.buttonTextColor || '#ffffff';
    
    return {
      container: {
        minHeight: "100vh",
        backgroundColor: backgroundColor,
        color: textColor,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 40px",
        borderBottom: `1px solid ${cardBorder}`
      },
      title: {
        fontSize: 24,
        fontWeight: 700,
        margin: 0,
        color: primaryColor
      },
      subtitle: {
        fontSize: 14,
        color: textSecondary
      },
      backButton: {
        padding: "8px 16px",
        background: isLightBackground ? '#f3f4f6' : "rgba(255,255,255,0.1)",
        color: textColor,
        textDecoration: "none",
        borderRadius: 6,
        fontSize: 14
      },
      content: {
        maxWidth: 1000,
        margin: "0 auto",
        padding: "40px 20px"
      },
      card: {
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 12,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        transition: "border-color 0.2s"
      },
      cardButton: {
        padding: "10px 20px",
        background: buttonBg,
        color: buttonText,
        border: "none",
        borderRadius: 6,
        fontWeight: 600,
        cursor: "pointer",
        textDecoration: "none",
        textAlign: "center",
        display: "inline-block"
      },
      welcomeTitle: {
        fontSize: 28,
        marginBottom: 10,
        color: textColor
      },
      welcomeText: {
        color: textSecondary,
        marginBottom: 32
      },
      textColor,
      textSecondary,
      cardBg,
      cardBorder,
      primaryColor
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);
  
  // Generate correct paths based on routing mode
  const getPath = (path) => {
    if (isSubdomain) {
      return `/admin${path}`;
    } else {
      return `/client/${tenantKey}${path}`;
    }
  };

  // Set document title
  useEffect(() => {
    if (tenant) {
      const tenantName = tenant.branding?.companyName || tenant.name || tenantKey;
      document.title = `Invenire - ${tenantName} - Dashboard`;
    }
  }, [tenant, tenantKey]);

  // Track page view
  useEffect(() => {
    const pagePath = isSubdomain ? '/admin' : '/client/[key]';
    trackPageView(pagePath, 'Client Dashboard');
  }, [isSubdomain]);
  
  useEffect(() => {
    if (!tenantKey) {
      setError("No tenant specified");
      setLoading(false);
      return;
    }
    
    async function loadTenant() {
      try {
        const response = await fetch(`${API_BASE}/tenants/${tenantKey}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Client "${tenantKey}" not found`);
          }
          throw new Error("Failed to load client");
        }
        const data = await response.json();
        setTenant(data);
        
        // Load jobs and stats for this tenant
        const summaryResponse = await fetch(`${API_BASE}/scoring/summary/tenant/${data.id}`);
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          const jobs = summaryData.jobs || [];
          setRecentJobs(jobs.slice(0, 5));
          
          // Calculate real stats from jobs data
          const totalInterviews = jobs.reduce((sum, j) => sum + (j.interview_count || 0), 0);
          const analyzedCount = jobs.reduce((sum, j) => sum + (j.analyzed_count || 0), 0);
          
          // Calculate average score from jobs that have scores
          const jobsWithScores = jobs.filter(j => j.avg_score != null || j.top_score != null);
          const avgScore = jobsWithScores.length > 0
            ? jobsWithScores.reduce((sum, j) => sum + (j.avg_score ?? j.top_score), 0) / jobsWithScores.length
            : null;
          
          setStats({
            totalInterviews,
            completedInterviews: analyzedCount,
            pendingInterviews: totalInterviews - analyzedCount,
            averageScore: avgScore
          });
        } else {
          // Fallback to basic jobs endpoint if summary not available
          const jobsResponse = await fetch(`${API_BASE}/jobs/tenant/${data.id}`);
          if (jobsResponse.ok) {
            const jobsData = await jobsResponse.json();
            setRecentJobs((jobsData.jobs || []).slice(0, 5));
          }
          setStats({
            totalInterviews: 0,
            completedInterviews: 0,
            pendingInterviews: 0,
            averageScore: null
          });
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    loadTenant();
  }, [tenantKey]);
  
  if (loading) {
    return (
      <div style={dynamicStyles.container}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: dynamicStyles.textSecondary }}>Loading...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.header}>
          <h1 style={dynamicStyles.title}>Invenire</h1>
          <span style={dynamicStyles.subtitle}>Error</span>
        </div>
        <div style={dynamicStyles.content}>
          <h2 style={{ fontSize: 24, marginBottom: 10, color: dynamicStyles.textColor }}>Client Not Found</h2>
          <p style={{ color: '#ef4444', marginBottom: 20 }}>{error}</p>
          <Link to="/" style={{ color: dynamicStyles.primaryColor, textDecoration: 'none' }}>← Back to Home</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <div style={dynamicStyles.header}>
        <div>
          <h1 style={dynamicStyles.title}>{companyName}</h1>
          <span style={dynamicStyles.subtitle}>Admin Dashboard</span>
        </div>
        <Link to="/" style={dynamicStyles.backButton}>Back to Site</Link>
      </div>
      
      {/* Main Content */}
      <div style={dynamicStyles.content}>
        {/* Plan Usage Widget - Top of Dashboard */}
        {tenant?.id && (
          <div style={styles.planUsageSection}>
            <PlanUsage tenantId={tenant.id} />
          </div>
        )}
        
        <h2 style={dynamicStyles.welcomeTitle}>Welcome to {companyName} Admin</h2>
        <p style={dynamicStyles.welcomeText}>
          Manage your interview configuration, upload candidates, and review results.
        </p>
        
        {/* Quick Actions */}
        <div style={styles.cardsGrid}>
          {/* Load Jobs */}
          <Link to={getPath("/jobs")} style={{ textDecoration: "none" }}>
            <div style={{ ...dynamicStyles.card, borderColor: primaryColor }}>
              <span style={{ ...styles.cardLabel, color: primaryColor }}>CONFIGURATION</span>
              <h3 style={{ ...styles.cardTitle, color: dynamicStyles.textColor }}>Load Jobs</h3>
              <p style={{ ...styles.cardText, color: dynamicStyles.textSecondary }}>
                Import job postings from URLs. AI extracts titles, requirements, and responsibilities.
              </p>
              <div style={dynamicStyles.cardButton}>
                Load Jobs
              </div>
            </div>
          </Link>
          
          {/* Upload Candidates */}
          <Link to={getPath("/candidates")} style={{ textDecoration: "none" }}>
            <div style={{ ...dynamicStyles.card, borderColor: COLORS.blue }}>
              <span style={{ ...styles.cardLabel, color: COLORS.blue }}>INTERVIEWS</span>
              <h3 style={{ ...styles.cardTitle, color: dynamicStyles.textColor }}>Upload Candidates</h3>
              <p style={{ ...styles.cardText, color: dynamicStyles.textSecondary }}>
                Add new candidates and generate interview invitations.
              </p>
              <div style={{ ...dynamicStyles.cardButton, backgroundColor: COLORS.blue, color: "#fff" }}>
                Upload Candidates
              </div>
            </div>
          </Link>
          
          {/* Reports & Insights */}
          <Link to={getPath("/reports")} style={{ textDecoration: "none" }}>
            <div style={{ ...dynamicStyles.card, borderColor: COLORS.purple }}>
              <span style={{ ...styles.cardLabel, color: COLORS.purple }}>INSIGHTS</span>
              <h3 style={{ ...styles.cardTitle, color: dynamicStyles.textColor }}>Reports & Analytics</h3>
              <p style={{ ...styles.cardText, color: dynamicStyles.textSecondary }}>
                View candidate scores, interview results, and AI-powered insights.
              </p>
              <div style={{ ...dynamicStyles.cardButton, backgroundColor: COLORS.purple, color: "#fff" }}>
                View Reports
              </div>
            </div>
          </Link>
          
          {/* Configuration */}
          <Link to={getPath("/config")} style={{ textDecoration: "none" }}>
            <div style={{ ...dynamicStyles.card, borderColor: COLORS.red }}>
              <span style={{ ...styles.cardLabel, color: COLORS.red }}>SETTINGS</span>
              <h3 style={{ ...styles.cardTitle, color: dynamicStyles.textColor }}>Configuration</h3>
              <p style={{ ...styles.cardText, color: dynamicStyles.textSecondary }}>
                Customize branding, interview scripts, and avatar settings.
              </p>
              <div style={{ ...dynamicStyles.cardButton, backgroundColor: COLORS.red }}>
                Configure
              </div>
            </div>
          </Link>
          
          {/* User Management - only show if authentication is required */}
          {tenant.require_auth && (
            <Link to={getPath("/users")} style={{ textDecoration: "none" }}>
              <div style={{ ...dynamicStyles.card, borderColor: COLORS.orange }}>
                <span style={{ ...styles.cardLabel, color: COLORS.orange }}>ACCESS CONTROL</span>
                <h3 style={{ ...styles.cardTitle, color: dynamicStyles.textColor }}>User Management</h3>
                <p style={{ ...styles.cardText, color: dynamicStyles.textSecondary }}>
                  Manage team members, approve access requests, and assign roles.
                </p>
                <div style={{ ...dynamicStyles.cardButton, backgroundColor: COLORS.orange }}>
                  Manage Users
                </div>
              </div>
            </Link>
          )}
        </div>
        
        {/* Quick Access: Job Reports */}
        {recentJobs.length > 0 && (
          <div style={styles.quickAccessSection}>
            <h3 style={{ ...styles.quickAccessTitle, color: dynamicStyles.textColor }}>Quick Access: Job Reports</h3>
            <div style={styles.quickAccessButtons}>
              {recentJobs.map(job => (
                <Link 
                  key={job.id} 
                  to={getPath(`/jobs/${job.id}/report`)}
                  style={{ ...styles.quickAccessButton, background: dynamicStyles.cardBg, color: dynamicStyles.textColor, border: `1px solid ${dynamicStyles.cardBorder}` }}
                >
                  📊 {job.title?.slice(0, 25) || `Job ${job.id}`}
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Stats (if available) */}
        {stats && (
          <div style={styles.statsSection}>
            <h3 style={{ ...styles.statsTitle, color: dynamicStyles.textColor }}>Overview</h3>
            <div style={styles.statsGrid}>
              <div style={{ ...styles.statCard, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
                <div style={{ ...styles.statNumber, color: dynamicStyles.textColor }}>{stats.totalInterviews}</div>
                <div style={{ ...styles.statLabel, color: dynamicStyles.textSecondary }}>Total Interviews</div>
              </div>
              <div style={{ ...styles.statCard, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
                <div style={{ ...styles.statNumber, color: dynamicStyles.textColor }}>{stats.completedInterviews}</div>
                <div style={{ ...styles.statLabel, color: dynamicStyles.textSecondary }}>Completed</div>
              </div>
              <div style={{ ...styles.statCard, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
                <div style={{ ...styles.statNumber, color: dynamicStyles.textColor }}>{stats.pendingInterviews}</div>
                <div style={{ ...styles.statLabel, color: dynamicStyles.textSecondary }}>Pending</div>
              </div>
              <div style={{ ...styles.statCard, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
                <div style={{ ...styles.statNumber, color: dynamicStyles.textColor }}>
                  {stats.averageScore !== null ? stats.averageScore.toFixed(1) : "—"}
                </div>
                <div style={{ ...styles.statLabel, color: dynamicStyles.textSecondary }}>Avg Score</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: "#3ECF8E"
  },
  subtitle: {
    fontSize: 14,
    color: "#9ca3af"
  },
  backButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    textDecoration: "none",
    borderRadius: 6,
    fontSize: 14
  },
  content: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "40px 20px"
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    fontSize: 18,
    color: "#9ca3af"
  },
  errorTitle: {
    fontSize: 24,
    marginBottom: 10
  },
  errorText: {
    color: "#ef4444",
    marginBottom: 20
  },
  backLink: {
    color: "#3ECF8E",
    textDecoration: "none"
  },
  planUsageSection: {
    marginBottom: 32
  },
  welcomeTitle: {
    fontSize: 28,
    marginBottom: 10
  },
  welcomeText: {
    color: "#9ca3af",
    marginBottom: 40,
    fontSize: 16
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
    marginBottom: 40
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: 24,
    transition: "transform 0.2s, border-color 0.2s",
    cursor: "pointer"
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    letterSpacing: 1
  },
  cardTitle: {
    fontSize: 20,
    margin: "12px 0 8px",
    color: "#fff"
  },
  cardText: {
    color: "#9ca3af",
    fontSize: 14,
    marginBottom: 20,
    lineHeight: 1.5
  },
  cardButton: {
    display: "inline-block",
    padding: "10px 20px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 14
  },
  quickAccessSection: {
    marginBottom: 40
  },
  quickAccessTitle: {
    fontSize: 14,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 16
  },
  quickAccessButtons: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap"
  },
  quickAccessButton: {
    padding: "8px 16px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    color: "#fff",
    textDecoration: "none",
    fontSize: 14
  },
  statsSection: {
    marginTop: 40
  },
  statsTitle: {
    fontSize: 18,
    marginBottom: 20,
    color: "#9ca3af"
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 16
  },
  statCard: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    padding: 20,
    textAlign: "center"
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 700,
    color: "#fff"
  },
  statLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4
  }
};
